import {
    normalizeSchoolDocumentAudienceMode,
    normalizeSchoolDocumentNotificationMode,
    normalizeSchoolDocumentAssignmentType,
    SCHOOL_DOCUMENT_ASSIGNMENT_TYPE,
} from './schoolDocumentModes';

/**
 * Normalised school document list item (POST /school/documents) including audience fields.
 * @typedef {object} SchoolDocumentListItem
 * @property {number|string} id
 * @property {string} name
 * @property {boolean} strictRecipientsFiltering
 * @property {boolean} isWholeSchoolAudience
 * @property {number} [assignmentType] 1 Students, 2 Employees
 * @property {number} [audienceMode] 0 Static, 1 Dynamic
 * @property {number} [notificationMode] 0 None, 1 InitialOnly, 2 InitialAndNewEligible
 * @property {{ filterId: number, excluded: boolean, filterName: string|null }[]} [groupFilters]
 * @property {{ employeeId: number, employeeName: string|null }[]} [employeeAssignments]
 */

/**
 * @param {SchoolDocumentListItem} doc
 */
export function formatSchoolDocumentAudienceSummary(doc) {
    const groups = doc.groupFilters || [];
    const employeeAssignments = doc.employeeAssignments || [];
    const assignmentType = normalizeSchoolDocumentAssignmentType(doc.assignmentType);
    const wholeSchoolLabel = assignmentType === SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.employees
        ? 'All employee profiles'
        : 'All student profiles';

    if (doc.isWholeSchoolAudience) {
        return wholeSchoolLabel;
    }

    const parts = [];

    if (groups.length > 0) {
        parts.push(
            groups
                .map((g) =>
                    g.filterName != null && String(g.filterName).trim() !== ''
                        ? g.filterName
                        : `Filter ${g.filterId}`)
                .join(', ')
        );
    }

    if (employeeAssignments.length > 0) {
        parts.push(
            employeeAssignments
                .map((a) =>
                    a.employeeName != null && String(a.employeeName).trim() !== ''
                        ? a.employeeName
                        : `Employee ${a.employeeId}`)
                .join(', ')
        );
    }

    return parts.length > 0 ? parts.join('; ') : wholeSchoolLabel;
}

/**
 * Maps API list item + editor shape for {@link DocumentAudienceGroupFields}.
 * @param {SchoolDocumentListItem} doc
 */
export function schoolDocumentToAudienceEditorState(doc) {
    const groupFilters = (doc.groupFilters || []).map((g) => ({
        id: g.filterId,
        displayName:
            g.filterName != null && String(g.filterName).trim() !== ''
                ? g.filterName
                : `Filter ${g.filterId}`,
        excluded: !!g.excluded,
    }));

    const employeeFilters = (doc.employeeAssignments || []).map((a) => ({
        id: a.employeeId,
        label: a.employeeName != null && String(a.employeeName).trim() !== ''
            ? a.employeeName
            : `Employee ${a.employeeId}`,
        preferredForename: a.employeeName,
        preferredSurname: '',
    }));

    return {
        strictRecipientsFiltering: !!doc.strictRecipientsFiltering && groupFilters.length > 1,
        assignmentType: normalizeSchoolDocumentAssignmentType(doc.assignmentType),
        audienceMode: normalizeSchoolDocumentAudienceMode(doc.audienceMode),
        notificationMode: normalizeSchoolDocumentNotificationMode(doc.notificationMode),
        audienceSettings: {
            groupFilters,
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: [],
            employeeFilters,
        },
    };
}

/**
 * @param {{ id: number, excluded: boolean }[]} groupFilters
 * @param {boolean} strictRecipientsFiltering
 * @param {{ audienceMode?: number, notificationMode?: number, employeeFilters?: { id: number }[] }} [modes]
 */
export function buildUpdateSchoolDocumentAudienceBody(groupFilters, strictRecipientsFiltering, modes = {}) {
    const body = {
        groupFilters: groupFilters.map((f) => ({
            filterId: f.id,
            excluded: !!f.excluded,
        })),
        strictRecipientsFiltering:
            groupFilters.length > 1 ? !!strictRecipientsFiltering : false,
        employeeIds: (modes.employeeFilters || [])
            .map((employee) => employee.id)
            .filter((id) => id != null),
    };
    if (modes.audienceMode !== undefined) {
        body.audienceMode = modes.audienceMode;
    }
    if (modes.notificationMode !== undefined) {
        body.notificationMode = modes.notificationMode;
    }
    return body;
}
